import { useMemo, useState } from "react";
import SimpleBar from "simplebar-react";
import { DateTimeLabel } from "../../../../common/components/date-time-label/DateTimeLabel";
import {
  HourSummaryStatesEnum,
  LiquidatedOvertimeHourModel,
  UserApprovalFlow,
} from "../../models/liquidateOvertimeHours";
import { PAYROLL_NOVELTY_STATUS } from "../../models/payrollNoveltyStatus";
import {
  ModifyOverTimesModal,
  ModifyOverTimesModalProps,
} from "../modals/ModifyOverTimesModal";
import { StatusIndicator } from "../presenters/Indicator";

interface Props {
  dataList: Record<string, LiquidatedOvertimeHourModel[]>;
  tab?: string;
  onChangeSelection: (item: LiquidatedOvertimeHourModel) => void;
  reHidrate: () => Promise<void>;
  selectedList: LiquidatedOvertimeHourModel[];
}

export default function PayrollNoveltyTable({
  dataList,
  tab,
  onChangeSelection,
  selectedList,
  reHidrate,
}: Props) {
  const [modifyOverTimesModal, setModifyOverTimesModal] =
    useState<ModifyOverTimesModalProps>({} as ModifyOverTimesModalProps);

  const getTitle = useMemo(() => {
    return (keyGroupe: string) => {
      let title = keyGroupe;

      if (!tab) {
        return title;
      }

      switch (tab) {
        case "employee":
          const employee = `${dataList[keyGroupe]?.[0]?.fullName} - ${dataList[keyGroupe]?.[0]?.employeeDocument}`;
          return employee ? employee.toUpperCase() : title;

        case "date":
          return title.split("T")[0];

        case "status":
          return getStateText(title);

        default:
          return title;
      }
    };
  }, [dataList, tab]);

  const getStateText = (state: HourSummaryStatesEnum | string): string => {
    let stateEnum: HourSummaryStatesEnum;

    if (typeof state === "string") {
      stateEnum =
        HourSummaryStatesEnum[state as keyof typeof HourSummaryStatesEnum];

      if (stateEnum === undefined) {
        return "Estado desconocido";
      }
    } else {
      stateEnum = state;
    }

    switch (stateEnum) {
      case HourSummaryStatesEnum.Draft:
        return "Borrador";
      case HourSummaryStatesEnum.Review:
        return "Revisión";
      case HourSummaryStatesEnum.Approved:
        return "Aprobado";
      case HourSummaryStatesEnum.Rejected:
        return "Rechazado";
      case HourSummaryStatesEnum.Undefined:
        return "Indefinido";
      default:
        return "Estado desconocido";
    }
  };

  const onCloseRejectOverTimesModal = () => {
    setModifyOverTimesModal((prev) => ({ ...prev, isOpen: false }));
  };

  const openModifyOverTimesModal = (selected: LiquidatedOvertimeHourModel) => {
    setModifyOverTimesModal({
      isOpen: true,
      onClose: onCloseRejectOverTimesModal,
      data: selected,
      reHidrate: reHidrate,
    });
  };

  const renderStatusFlow = (usersApprovalFlows: UserApprovalFlow[]) => {
    return (
      <div className="dropdown">
        <button
          className="btn btn-sm btn-light dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          Ver
        </button>
        <ul className="dropdown-menu">
          {usersApprovalFlows?.map((user, index) => {
            return (
              <div
                key={index}
                className="d-flex aling-items-center gap-1 mb-1 p-1"
              >
                <StatusIndicator
                  status={user.status ?? 0}
                  statusList={PAYROLL_NOVELTY_STATUS}
                  showName={false}
                />
                <div className="text-dark" style={{ fontSize: 10 }}>
                  {user.userName}
                </div>
              </div>
            );
          })}
        </ul>
      </div>
    );
  };

  const handleSelectAllGroup = (groupKey: string, isChecked: boolean) => {
    const groupItems = dataList[groupKey] ?? [];
    groupItems.forEach((item) => {
      const isSelected = selectedList.includes(item);
      if (isChecked && !isSelected) onChangeSelection(item);
      if (!isChecked && isSelected) onChangeSelection(item);
    });
  };

  const isGroupAllSelected = (groupKey: string) =>
    (dataList[groupKey] ?? []).every((item) => selectedList.includes(item));

  const isGroupIndeterminate = (groupKey: string) => {
    const groupItems = dataList[groupKey] ?? [];
    const selectedCount = groupItems.filter((item) =>
      selectedList.includes(item),
    ).length;
    return selectedCount > 0 && selectedCount < groupItems.length;
  };

  return (
    <>
      {modifyOverTimesModal.isOpen && (
        <ModifyOverTimesModal {...modifyOverTimesModal} />
      )}

      <div>
        {Object.entries(dataList).map(([key, items]) => (
          <div key={key} className="card border my-3">
            <div className="card-header">
              <h5 className="mb-0">{getTitle(key)}</h5>
            </div>
            <div className="card-body p-1 ">
              <SimpleBar direction="ltr">
                <div className="mx-2 no-listing">
                  <div className="table-responsive">
                    <table className="table align-middle table-nowrap text-black">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center checked-custom">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={isGroupAllSelected(key)}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = isGroupIndeterminate(key);
                                }
                              }}
                              onChange={(e) =>
                                handleSelectAllGroup(key, e.target.checked)
                              }
                            />
                          </th>

                          {tab !== "employee" && <th>Empleado</th>}

                          <th>Turno</th>
                          {tab !== "date" && <th>Fecha</th>}
                          <th>
                            Hora de <br></br> inicio liquidación
                          </th>
                          <th>
                            Hora de <br></br> fin liquidación
                          </th>
                          <th>Tipo de hora</th>
                          <th>
                            Horas<br></br> calculadas
                          </th>
                          <th>
                            Horas<br></br> aprobadas
                          </th>
                          <th>Observaciones</th>
                          <th>Motivo de aprobación</th>
                          <th>Aprobadores</th>
                          {tab !== "status" && <th>Estado</th>}

                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>

                      <tbody className="pt-5 mt-4">
                        {items.map((item, i) => (
                          <tr key={i}>
                            <td className="text-dark text-center checked-custom">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedList.includes(item)}
                                onChange={() => onChangeSelection(item)}
                              />
                            </td>
                            {tab !== "employee" && (
                              <td>
                                {item.fullName} - {item.employeeDocument}
                              </td>
                            )}
                            <td>{item.workshiftName}</td>
                            {tab !== "date" && (
                              <td>
                                <DateTimeLabel
                                  date={item.date}
                                  showTime={false}
                                />
                              </td>
                            )}
                            <td>
                              <DateTimeLabel
                                date={item.startDate}
                                showTime={true}
                              />
                            </td>
                            <td>
                              <DateTimeLabel
                                date={item.endDate}
                                showTime={true}
                              />
                            </td>
                            <td>{item.abbreviation}</td>

                            <td>
                              {item.hour}:{item.minute}
                            </td>

                            <td>
                              {item.modifiedHour != null &&
                                item.modifiedMinute != null &&
                                `${item.modifiedHour}:${item.modifiedMinute}`}
                            </td>
                            <td>{item.comments}</td>
                            <td>{item.overtimeReason?.name}</td>
                            <td>{renderStatusFlow(item.usersApprovalFlows)}</td>

                            {tab !== "status" && (
                              <td>
                                <StatusIndicator
                                  status={item.status ?? 0}
                                  statusList={PAYROLL_NOVELTY_STATUS}
                                  showName
                                />
                              </td>
                            )}
                            <td
                              className="text-end"
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => openModifyOverTimesModal(item)}
                            >
                              modificar
                              <i className="ri-edit-2-fill"></i>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </SimpleBar>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
